import React, { useState, useEffect } from 'react';
// Added Grid hook for responsiveness
import { Card, Button, Table, Modal, Form, Input, DatePicker, Space, Tag, Typography, message, List, Tooltip, Divider, Avatar, Grid } from 'antd';
import { PlusOutlined, MinusCircleOutlined, CheckCircleOutlined, InfoCircleOutlined, UserOutlined, CommentOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid; // RESPONSIVE CHANGE

const Commitments = () => {
    const [commitments, setCommitments] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const screens = useBreakpoint(); // RESPONSIVE CHANGE

    const fetchCommitments = async () => {
        setLoading(true);
        try {
            const res = await api.get('/mentee/commitments');
            setCommitments(Array.isArray(res.data) ? res.data : []);
        } catch (e) { 
            setCommitments([]);
            message.error(e.response?.data?.message || 'Failed to load commitments'); 
        }
        setLoading(false);
    };

    useEffect(() => { fetchCommitments(); }, []);

    const markMilestoneDone = async (milestoneId) => {
        try {
            await api.patch(`/mentee/milestones/${milestoneId}`, { status: 'completed_by_mentee' });
            message.success('Milestone marked as complete!');
            fetchCommitments();
        } catch (e) {
            message.error(e.response?.data?.message || 'Failed to update milestone');
        }
    };

    const handleCreate = async (values) => {
        try {
            const payload = {
                ...values,
                milestones: values.milestones?.map(m => ({
                    ...m,
                    target_date: m.target_date?.format('YYYY-MM-DD')
                })) || []
            };
            await api.post('/mentee/commitments', payload);
            message.success('Commitment added!');
            setIsModalVisible(false);
            form.resetFields();
            fetchCommitments();
        } catch (e) { message.error('Failed to add commitment'); }
    };

    const columns = [
        { 
            title: 'Goal / Commitment', 
            dataIndex: 'title', 
            key: 'title',
            // RESPONSIVE CHANGE: Ensure text wraps nicely on small screens
            render: (text) => <Text strong style={{ whiteSpace: 'normal' }}>{text}</Text> 
        },
        // RESPONSIVE CHANGE: Hide Feedback/Progress columns on extra-small screens to save space
        // Users can see this in the expanded row anyway
        ...(screens.xs ? [] : [
            { 
                title: 'Feedback', 
                key: 'feedback',
                render: (_, record) => (
                    <Space><CommentOutlined /><Text>{record.comments?.length || 0}</Text></Space>
                )
            },
            { 
                title: 'Progress', 
                key: 'progress',
                render: (_, record) => {
                    const total = record.milestones?.length || 0;
                    const confirmed = record.milestones?.filter(m => m.status === 'confirmed_by_mentor').length || 0;
                    return <Tag color="orange">{confirmed}/{total}</Tag>;
                }
            }
        ]),
        { 
            title: 'Status', 
            dataIndex: 'status', 
            render: (status) => <Tag color={status === 'open' ? 'blue' : 'green'}>{status.toUpperCase()}</Tag>
        }
    ];

    const expandedRowRender = (record) => (
        // RESPONSIVE CHANGE: Reduce padding for mobile
        <div style={{ padding: screens.xs ? '0 5px' : '0 20px', background: '#fafafa', borderRadius: '8px' }}>
            <Divider orientation="left"><Text strong>Milestones</Text></Divider>
            <List
                itemLayout="horizontal"
                dataSource={record.milestones}
                renderItem={(m) => (
                    <List.Item
                        actions={[
                            m.status === 'pending' ? (
                                <Button 
                                    type="primary" 
                                    size="small" 
                                    icon={<CheckCircleOutlined />} 
                                    onClick={() => markMilestoneDone(m.id)}
                                >
                                    {screens.xs ? '' : 'Mark Done'} 
                                </Button>
                            ) : <Tag color={m.status === 'completed_by_mentee' ? 'processing' : 'success'}>
                                {screens.xs ? 'Wait' : 'Confirmed'}
                            </Tag>
                        ]}
                    >
                        <List.Item.Meta
                            title={m.title}
                            description={m.target_date ? moment(m.target_date).format('MMM DD') : ''}
                        />
                    </List.Item>
                )}
            />

            <Divider orientation="left"><Text strong><CommentOutlined /> Feedback</Text></Divider>
            <List
                itemLayout="horizontal"
                dataSource={record.comments || []}
                renderItem={(item) => (
                    <List.Item>
                        <List.Item.Meta
                            avatar={<Avatar size="small" icon={<UserOutlined />} />}
                            title={item.user?.name}
                            description={item.content}
                        />
                    </List.Item>
                )}
            />
        </div>
    );

    return (
        <Card 
            title={screens.xs ? "Goals" : "My Journey & Commitments"} 
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                    {screens.xs ? '' : 'Add Goal'}
                </Button>
            }
        >
            <Table 
                dataSource={commitments} 
                columns={columns} 
                rowKey="id" 
                loading={loading}
                // RESPONSIVE CHANGE: scroll allows the table to shift on small screens
                scroll={{ x: 'max-content' }}
                expandable={{ expandedRowRender }}
            />

            <Modal 
                title="Add Goal" 
                open={isModalVisible} 
                onCancel={() => setIsModalVisible(false)} 
                onOk={() => form.submit()} 
                // RESPONSIVE CHANGE: Modal width 100% on mobile
                width={screens.xs ? '95%' : 600}
            >
                <Form form={form} layout="vertical" onFinish={handleCreate}>
                    <Form.Item name="title" label="Goal Title" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Master Bible Study" />
                    </Form.Item>
                    
                    <Form.List name="milestones" initialValue={[{ title: '', target_date: null }]}>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    // RESPONSIVE CHANGE: Stack inputs vertically on mobile using wrap
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline" wrap={screens.xs}>
                                        <Form.Item {...restField} name={[name, 'title']} rules={[{ required: true }]} style={{flex: 1}}>
                                            <Input placeholder="Milestone" />
                                        </Form.Item>
                                        <Form.Item {...restField} name={[name, 'target_date']}>
                                            <DatePicker placeholder="Date" style={{width: screens.xs ? '100%' : 'auto'}} />
                                        </Form.Item>
                                        {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} />}
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Milestone</Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </Card>
    );
};

export default Commitments;